import type { GridGeometry } from '../../grid';
import { resolveTileDrawMetrics } from '../tilemap/board';
import { Viewport } from '../tilemap/viewport';

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface WorldBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

interface World2DCameraOptions {
  readonly viewportWidthPx: number;
  readonly viewportHeightPx: number;
  readonly basePixelsPerWorldUnit?: number;
  readonly zoomFactor?: number;
  readonly minZoomFactor?: number;
  readonly maxZoomFactor?: number;
  readonly centerX?: number;
  readonly centerY?: number;
}

const DEFAULT_BASE_PIXELS_PER_WORLD_UNIT = 64;
const DEFAULT_MIN_ZOOM_FACTOR = 0.25;
const DEFAULT_MAX_ZOOM_FACTOR = 6;

export class World2DCamera {
  private viewportWidthPx: number;
  private viewportHeightPx: number;
  private readonly basePixelsPerWorldUnit: number;
  private readonly minZoomFactor: number;
  private readonly maxZoomFactor: number;
  private zoomFactor: number;
  private centerTileX: number;
  private centerTileY: number;
  private readonly viewport: Viewport;

  constructor(options: World2DCameraOptions) {
    this.viewportWidthPx = Math.max(1, Math.round(options.viewportWidthPx));
    this.viewportHeightPx = Math.max(1, Math.round(options.viewportHeightPx));
    this.basePixelsPerWorldUnit = options.basePixelsPerWorldUnit ?? DEFAULT_BASE_PIXELS_PER_WORLD_UNIT;
    this.minZoomFactor = options.minZoomFactor ?? DEFAULT_MIN_ZOOM_FACTOR;
    this.maxZoomFactor = options.maxZoomFactor ?? DEFAULT_MAX_ZOOM_FACTOR;
    this.zoomFactor = this.clampZoomFactor(options.zoomFactor ?? 1);
    this.centerTileX = options.centerX ?? 0;
    this.centerTileY = options.centerY ?? 0;
    this.viewport = new Viewport(this.getPixelsPerWorldUnit());
    this.syncViewportRect();
  }

  public setViewportSize(widthPx: number, heightPx: number): void {
    const preservedCenterWorld = this.viewport.screenToWorld(this.viewportWidthPx / 2, this.viewportHeightPx / 2);
    this.viewportWidthPx = Math.max(1, Math.round(widthPx));
    this.viewportHeightPx = Math.max(1, Math.round(heightPx));
    this.syncViewportRect();
    this.viewport.setCameraOffset(
      (this.viewportWidthPx / 2) - preservedCenterWorld.x,
      (this.viewportHeightPx / 2) - preservedCenterWorld.y,
    );
  }

  public setCenter(point: WorldPoint, grid: GridGeometry): void {
    this.centerTileX = point.x;
    this.centerTileY = point.y;
    const focusCenter = this.resolveWorldCenter(point.x, point.y, grid);
    this.setCenterFromWorldPoint(focusCenter);
  }

  public setCenterFromWorldPoint(center: WorldPoint): void {
    this.viewport.setCameraOffset(
      (this.viewportWidthPx / 2) - center.x,
      (this.viewportHeightPx / 2) - center.y,
    );
  }

  public getCenter(): WorldPoint {
    return { x: this.centerTileX, y: this.centerTileY };
  }

  public getWorldCenter(): WorldPoint {
    return this.viewport.screenToWorld(this.viewportWidthPx / 2, this.viewportHeightPx / 2);
  }

  public setZoomFactor(nextZoomFactor: number): void {
    const preservedCenterWorld = this.viewport.screenToWorld(this.viewportWidthPx / 2, this.viewportHeightPx / 2);
    this.zoomFactor = this.clampZoomFactor(nextZoomFactor);
    this.viewport.setTileSize(this.getPixelsPerWorldUnit());
    this.viewport.setCameraOffset(
      (this.viewportWidthPx / 2) - preservedCenterWorld.x,
      (this.viewportHeightPx / 2) - preservedCenterWorld.y,
    );
  }

  public getZoomFactor(): number {
    return this.zoomFactor;
  }

  public getPixelsPerWorldUnit(): number {
    return this.basePixelsPerWorldUnit * this.zoomFactor;
  }

  public getViewportSize(): { width: number; height: number } {
    return {
      width: this.viewportWidthPx,
      height: this.viewportHeightPx,
    };
  }

  public worldPointToScreen(point: WorldPoint, _grid: GridGeometry): ScreenPoint {
    return this.viewport.worldCenterToScreen(point);
  }

  public tileOriginToScreen(tileX: number, tileY: number, grid: GridGeometry): ScreenPoint {
    return this.viewport.worldToScreen(tileX, tileY, grid);
  }

  public tileCenterToScreen(tileX: number, tileY: number, grid: GridGeometry): ScreenPoint {
    return this.viewport.worldToScreenCenter(tileX, tileY, grid);
  }

  public screenToWorld(point: ScreenPoint, _grid: GridGeometry): WorldPoint {
    return this.viewport.screenToWorld(point.x, point.y);
  }

  public screenToTile(point: ScreenPoint, grid: GridGeometry, mapWidth: number, mapHeight: number): WorldPoint | null {
    return this.viewport.screenToTile(point.x, point.y, grid, mapWidth, mapHeight);
  }

  public panByScreenDelta(deltaX: number, deltaY: number): void {
    const offset = this.viewport.getCameraOffset();
    this.viewport.setCameraOffset(offset.x + deltaX, offset.y + deltaY);
  }

  public zoomAboutScreenPoint(nextZoomFactor: number, anchor: ScreenPoint, grid: GridGeometry, mapWidth: number, mapHeight: number): void {
    const previousPixelsPerWorldUnit = this.getPixelsPerWorldUnit();
    const anchorWorld = this.screenToWorld(anchor, grid);
    const anchorTile = grid.pixelToTile(anchorWorld.x, anchorWorld.y, previousPixelsPerWorldUnit);
    const anchorOrigin = grid.tileToPixel(anchorTile.q, anchorTile.r, previousPixelsPerWorldUnit);
    const normalizedOffsetX = (anchorWorld.x - anchorOrigin.x) / previousPixelsPerWorldUnit;
    const normalizedOffsetY = (anchorWorld.y - anchorOrigin.y) / previousPixelsPerWorldUnit;

    this.zoomFactor = this.clampZoomFactor(nextZoomFactor);
    const nextPixelsPerWorldUnit = this.getPixelsPerWorldUnit();
    this.viewport.setTileSize(nextPixelsPerWorldUnit);
    const nextAnchorOrigin = grid.tileToPixel(anchorTile.q, anchorTile.r, nextPixelsPerWorldUnit);
    const nextAnchorWorld = {
      x: nextAnchorOrigin.x + normalizedOffsetX * nextPixelsPerWorldUnit,
      y: nextAnchorOrigin.y + normalizedOffsetY * nextPixelsPerWorldUnit,
    };
    this.viewport.setCameraOffset(anchor.x - nextAnchorWorld.x, anchor.y - nextAnchorWorld.y);

    const centerWorld = this.viewport.screenToWorld(this.viewportWidthPx / 2, this.viewportHeightPx / 2);
    const centerTile = grid.pixelToTile(centerWorld.x, centerWorld.y, nextPixelsPerWorldUnit);
    this.centerTileX = Math.max(0, Math.min(mapWidth - 1, centerTile.q));
    this.centerTileY = Math.max(0, Math.min(mapHeight - 1, centerTile.r));
  }

  public getVisibleWorldBounds(_grid: GridGeometry): WorldBounds {
    const rect = this.viewport.getVisibleWorldRect();
    return {
      minX: rect.left,
      minY: rect.top,
      maxX: rect.right,
      maxY: rect.bottom,
    };
  }

  public copyToViewport(viewport: Viewport): void {
    viewport.setContainerRect(new DOMRect(0, 0, this.viewportWidthPx, this.viewportHeightPx));
    viewport.setTileSize(this.getPixelsPerWorldUnit());
    const offset = this.viewport.getCameraOffset();
    viewport.setCameraOffset(offset.x, offset.y);
  }

  private resolveWorldCenter(tileX: number, tileY: number, grid: GridGeometry): WorldPoint {
    const tileMetrics = resolveTileDrawMetrics(grid, this.getPixelsPerWorldUnit(), tileX, tileY);
    const centroid = this.viewport.getInterpolatedTileCentroid(tileX, tileY, grid);
    return {
      x: tileMetrics.originX + centroid.x,
      y: tileMetrics.originY + centroid.y,
    };
  }

  private syncViewportRect(): void {
    this.viewport.setContainerRect(new DOMRect(0, 0, this.viewportWidthPx, this.viewportHeightPx));
    this.viewport.setTileSize(this.getPixelsPerWorldUnit());
  }

  private clampZoomFactor(value: number): number {
    if (!Number.isFinite(value)) {
      return 1;
    }
    return Math.min(this.maxZoomFactor, Math.max(this.minZoomFactor, value));
  }
}
